<template>
  <div class="multi-transport-page nx-page nx-report-page">
    <nx-report-header title="多维数据查询与智能分析">
      <div class="head-actions">
        <el-tag type="success" effect="plain">入厂</el-tag>
        <el-button
          type="primary"
          plain
          icon="el-icon-printer"
          :loading="exportLoading"
          :disabled="exportLoading"
          @click="handleExportPdf"
        >
          导出PDF
        </el-button>
      </div>
    </nx-report-header>

    <el-card shadow="never" class="filter-card">
      <div class="filter-title">筛选条件</div>
      <el-form inline :model="query" class="filter-form" size="small">
        <el-form-item label="车次">
          <el-input
            v-model.trim="query.trainNumber"
            clearable
            maxlength="50"
            placeholder="请输入车次"
            style="width: 180px"
            @keyup.enter.native="handleQuery"
          />
        </el-form-item>
        <el-form-item label="到达时间">
          <el-date-picker
            v-model="dateRange"
            type="daterange"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            value-format="yyyy-MM-dd"
            unlink-panels
            style="width: 260px"
          />
        </el-form-item>
        <el-form-item label="净重区间">
          <el-input-number
            v-model="query.minNetWeight"
            :min="0"
            :precision="2"
            :controls="false"
            placeholder="最小"
            class="weight-input"
          />
          <span class="range-separator">-</span>
          <el-input-number
            v-model="query.maxNetWeight"
            :min="0"
            :precision="2"
            :controls="false"
            placeholder="最大"
            class="weight-input"
          />
          <span class="weight-unit">吨</span>
        </el-form-item>
        <el-form-item label="煤流向">
          <el-select v-model="query.cargoFlow" clearable placeholder="全部" style="width: 150px">
            <el-option label="全部" value="" />
            <el-option label="仓库" value="1" />
            <el-option label="中卫热电" value="2" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" :loading="analysisLoading" @click="handleQuery">查询</el-button>
          <el-button icon="el-icon-refresh-left" @click="resetQuery">重置</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <el-row :gutter="16" class="kpi-row">
      <el-col v-for="item in kpiItems" :key="item.key" :xs="24" :sm="12" :md="6">
        <el-card shadow="hover" class="kpi-card" :class="item.className">
          <div class="kpi-icon"><i :class="item.icon" /></div>
          <div class="kpi-val">{{ item.value }}</div>
          <div class="kpi-label">{{ item.label }}</div>
          <div class="kpi-sub">{{ item.sub }}</div>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="16" class="chart-row">
      <el-col :xs="24" :lg="12">
        <el-card shadow="hover" class="block-card">
          <div class="card-head">
            <span class="card-title">按日期统计</span>
          </div>
          <div ref="dateChart" v-loading="analysisLoading" class="echart-box" />
        </el-card>
      </el-col>
      <el-col :xs="24" :lg="12">
        <el-card shadow="hover" class="block-card">
          <div class="card-head">
            <span class="card-title">按煤种统计</span>
          </div>
          <div ref="cargoChart" v-loading="analysisLoading" class="echart-box" />
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="16" class="chart-row">
      <el-col :xs="24" :lg="12">
        <el-card shadow="hover" class="block-card">
          <div class="card-head">
            <span class="card-title">按客户统计</span>
          </div>
          <div ref="customerChart" v-loading="analysisLoading" class="echart-box" />
        </el-card>
      </el-col>
      <el-col :xs="24" :lg="12">
        <el-card shadow="hover" class="block-card">
          <div class="card-head">
            <span class="card-title">按煤流向统计</span>
          </div>
          <div ref="coalFlowChart" v-loading="analysisLoading" class="echart-box" />
        </el-card>
      </el-col>
    </el-row>

    <el-card shadow="hover" class="block-card table-card">
      <div class="card-head">
        <span class="card-title">运输明细</span>
        <span class="table-tip">方向：入厂</span>
      </div>
      <el-table
        v-loading="tableLoading"
        :data="tableRecords"
        border
        stripe
        style="width: 100%"
        :empty-text="tableEmptyText"
      >
        <el-table-column prop="trainArrivalTime" label="到达时间" width="170" :formatter="fmtDateTime" />
        <el-table-column prop="trainNumber" label="车次" width="130" show-overflow-tooltip />
        <el-table-column prop="planNo" label="计划号" min-width="150" show-overflow-tooltip />
        <el-table-column prop="cargoName" label="煤种" min-width="120" show-overflow-tooltip />
        <el-table-column prop="custName" label="客户" min-width="140" show-overflow-tooltip />
        <el-table-column prop="cargoFlow" label="煤流向" width="110">
          <template slot-scope="{ row }">
            <el-tag :type="coalFlowTag(row)" size="small">{{ coalFlowLabel(row) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="flowTargetName" label="仓库/中卫热电" min-width="150" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ flowTargetText(row) }}</template>
        </el-table-column>
        <el-table-column prop="carriageCount" label="车厢数" width="90" align="right" :formatter="fmtInteger" />
        <el-table-column prop="netWeight" label="净重(吨)" width="110" align="right" :formatter="fmtTon" />
        <el-table-column prop="railwayInboundStatusName" label="入库状态" width="110" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ row.railwayInboundStatusName || row.inStockStatusName || row.stockStatusName || row.statusName || '-' }}</template>
        </el-table-column>
      </el-table>
      <el-pagination
        class="pager"
        background
        layout="total, sizes, prev, pager, next, jumper"
        :page-sizes="[10, 20, 50, 100]"
        :total="tableTotal"
        :page-size="pagination.size"
        :current-page.sync="pagination.page"
        @size-change="handleSizeChange"
        @current-change="loadTable"
      />
    </el-card>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import NxReportHeader from '@/components/nx/report/NxReportHeader.vue'
import { fetchAnalysis, fetchDetailPage, exportPdf } from './api'

function fmtDateStr (date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

function normalizeList (value) {
  if (!value) return []
  if (Array.isArray(value)) return value
  if (Array.isArray(value.records)) return value.records
  if (Array.isArray(value.list)) return value.list
  if (Array.isArray(value.data)) return value.data
  return []
}

function pickNumber (source, keys) {
  if (!source) return 0
  for (let i = 0; i < keys.length; i++) {
    const value = source[keys[i]]
    if (value !== null && value !== undefined && value !== '') {
      const num = Number(value)
      return Number.isNaN(num) ? 0 : num
    }
  }
  return 0
}

export default {
  name: 'RailwayMultiTransportData',
  components: { NxReportHeader },
  data () {
    const end = new Date()
    const start = new Date()
    start.setDate(end.getDate() - 6)
    return {
      dateRange: [fmtDateStr(start), fmtDateStr(end)],
      query: {
        trainNumber: '',
        minNetWeight: undefined,
        maxNetWeight: undefined,
        cargoFlow: ''
      },
      pagination: {
        page: 1,
        size: 10
      },
      summary: {},
      dateStats: [],
      cargoStats: [],
      customerStats: [],
      coalFlowStats: [],
      tableRecords: [],
      tableTotal: 0,
      analysisLoading: false,
      tableLoading: false,
      exportLoading: false,
      hasQueried: false,
      dateChart: null,
      cargoChart: null,
      customerChart: null,
      coalFlowChart: null,
      chartColors: ['#409EFF', '#67C23A', '#E6A23C', '#F56C6C', '#909399', '#9b59b6']
    }
  },
  computed: {
    kpiItems () {
      return [
        {
          key: 'totalTrains',
          label: '总车次',
          value: this.formatInteger(this.summary.totalTrains || this.summary.trainCount || this.tableTotal),
          sub: '筛选范围内',
          icon: 'el-icon-s-order',
          className: 'kpi-blue'
        },
        {
          key: 'totalNetWeight',
          label: '总净重',
          value: this.formatTonValue(this.summary.totalNetWeight || this.summary.totalWeight || this.summary.netWeightSum),
          sub: '吨',
          icon: 'el-icon-data-line',
          className: 'kpi-green'
        },
        {
          key: 'avgNetWeight',
          label: '平均净重',
          value: this.formatTonValue(this.summary.avgNetWeight || this.summary.averageNetWeight || this.summary.avgWeight),
          sub: '吨/车次',
          icon: 'el-icon-odometer',
          className: 'kpi-gold'
        },
        {
          key: 'maxNetWeight',
          label: '最大净重',
          value: this.formatTonValue(this.summary.maxNetWeight || this.summary.maxWeight),
          sub: '单车次',
          icon: 'el-icon-top-right',
          className: 'kpi-red'
        }
      ]
    },
    tableEmptyText () {
      return this.hasQueried ? '暂无匹配的铁路入厂数据' : '请先查询'
    }
  },
  mounted () {
    this.$nextTick(() => {
      this.dateChart = echarts.init(this.$refs.dateChart)
      this.cargoChart = echarts.init(this.$refs.cargoChart)
      this.customerChart = echarts.init(this.$refs.customerChart)
      this.coalFlowChart = echarts.init(this.$refs.coalFlowChart)
      window.addEventListener('resize', this.onResize)
      this.handleQuery()
    })
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.onResize)
    const charts = [this.dateChart, this.cargoChart, this.customerChart, this.coalFlowChart]
    charts.forEach((chart) => {
      chart && chart.dispose()
    })
  },
  methods: {
    buildPayload () {
      if (!this.dateRange || this.dateRange.length !== 2) {
        this.$message.warning('请选择到达时间范围')
        return null
      }
      if (
        this.query.minNetWeight !== undefined &&
        this.query.minNetWeight !== null &&
        this.query.maxNetWeight !== undefined &&
        this.query.maxNetWeight !== null &&
        Number(this.query.minNetWeight) > Number(this.query.maxNetWeight)
      ) {
        this.$message.warning('净重区间最小值不能大于最大值')
        return null
      }
      return {
        trainNumber: this.query.trainNumber || undefined,
        startTime: `${this.dateRange[0]} 00:00:00`,
        endTime: `${this.dateRange[1]} 23:59:59`,
        minNetWeight:
          this.query.minNetWeight !== undefined && this.query.minNetWeight !== null
            ? this.query.minNetWeight
            : undefined,
        maxNetWeight:
          this.query.maxNetWeight !== undefined && this.query.maxNetWeight !== null
            ? this.query.maxNetWeight
            : undefined,
        cargoFlow: this.query.cargoFlow || undefined
      }
    },
    handleQuery () {
      this.pagination.page = 1
      this.loadAll()
    },
    resetQuery () {
      const end = new Date()
      const start = new Date()
      start.setDate(end.getDate() - 6)
      this.dateRange = [fmtDateStr(start), fmtDateStr(end)]
      this.query = {
        trainNumber: '',
        minNetWeight: undefined,
        maxNetWeight: undefined,
        cargoFlow: ''
      }
      this.handleQuery()
    },
    async loadAll () {
      const base = this.buildPayload()
      if (!base) return
      this.hasQueried = true
      await Promise.all([this.loadAnalysis(base), this.loadTable(base)])
    },
    async loadAnalysis (basePayload) {
      const payload = basePayload || this.buildPayload()
      if (!payload) return
      this.analysisLoading = true
      try {
        const { data } = await fetchAnalysis(payload)
        const wrap = (data && data.data) || {}
        this.summary = wrap.summary || wrap.kpi || wrap
        this.dateStats = normalizeList(wrap.dateStats || wrap.trend || wrap.dateTrend || wrap.arrivalTrend)
        this.cargoStats = normalizeList(wrap.cargoStats || wrap.cargoDistribution || wrap.cargoList)
        this.customerStats = normalizeList(wrap.custStats || wrap.customerStats || wrap.customerDistribution || wrap.customerList)
        this.coalFlowStats = normalizeList(wrap.cargoFlowStats || wrap.coalFlowStats || wrap.coalFlowDistribution || wrap.flowStats)
        this.renderCharts()
      } catch (e) {
        this.summary = {}
        this.dateStats = []
        this.cargoStats = []
        this.customerStats = []
        this.coalFlowStats = []
        this.renderCharts()
        this.$message.error('分析数据加载失败')
      } finally {
        this.analysisLoading = false
      }
    },
    async loadTable (basePayload) {
      const payload = basePayload || this.buildPayload()
      if (!payload) return
      this.tableLoading = true
      try {
        const { data } = await fetchDetailPage({
          ...payload,
          page: this.pagination.page,
          size: this.pagination.size
        })
        const page = (data && data.data) || {}
        this.tableRecords = page.records || page.list || []
        this.tableTotal = page.total || 0
      } catch (e) {
        this.tableRecords = []
        this.tableTotal = 0
        this.$message.error('明细数据加载失败')
      } finally {
        this.tableLoading = false
      }
    },
    handleSizeChange (size) {
      this.pagination.size = size
      this.pagination.page = 1
      this.loadTable()
    },
    renderCharts () {
      this.renderDateChart()
      this.renderBarChart(this.cargoChart, this.cargoStats, '煤种', '净重(吨)')
      this.renderBarChart(this.customerChart, this.customerStats, '客户', '净重(吨)')
      this.renderCoalFlowChart()
    },
    renderDateChart () {
      if (!this.dateChart) return
      const list = this.dateStats || []
      const titles = list.map((item) => item.dimensionName || item.title || item.date || item.statDate || item.name || '-')
      const weights = list.map((item) => pickNumber(item, ['netWeight', 'netWeightSum', 'totalNetWeight', 'totalWeight', 'weight']))
      const counts = list.map((item) => pickNumber(item, ['trainCount', 'totalTrains', 'count']))
      this.dateChart.setOption(
        {
          color: ['#409EFF', '#67C23A'],
          tooltip: { trigger: 'axis' },
          legend: { data: ['净重(吨)', '车次'] },
          grid: { left: '3%', right: '4%', bottom: '3%', top: '18%', containLabel: true },
          xAxis: { type: 'category', data: titles, boundaryGap: false },
          yAxis: [
            { type: 'value', name: '吨', splitLine: { lineStyle: { type: 'dashed' } } },
            { type: 'value', name: '车次', splitLine: { show: false } }
          ],
          series: [
            {
              name: '净重(吨)',
              type: 'line',
              smooth: true,
              areaStyle: { color: 'rgba(64,158,255,0.1)' },
              data: weights
            },
            {
              name: '车次',
              type: 'bar',
              yAxisIndex: 1,
              barMaxWidth: 24,
              data: counts
            }
          ]
        },
        true
      )
    },
    renderBarChart (chart, list, labelName, valueName) {
      if (!chart) return
      const source = list || []
      const titles = source.map((item) => item.dimensionName || item.title || item.name || item.cargoName || item.custName || item.customerName || '-')
      const values = source.map((item) => pickNumber(item, ['netWeight', 'netWeightSum', 'totalNetWeight', 'totalWeight', 'weight']))
      chart.setOption(
        {
          tooltip: { trigger: 'axis' },
          grid: { left: 54, right: 24, bottom: 36, top: 52, containLabel: true },
          xAxis: { type: 'category', data: titles, axisLabel: { rotate: titles.length > 5 ? 30 : 0 } },
          yAxis: {
            type: 'value',
            name: valueName,
            nameGap: 18,
            nameTextStyle: { padding: [0, 0, 0, 12] }
          },
          series: [
            {
              name: labelName,
              type: 'bar',
              barMaxWidth: 36,
              data: values.map((value, index) => ({
                value,
                itemStyle: { color: this.chartColors[index % this.chartColors.length] }
              }))
            }
          ]
        },
        true
      )
    },
    renderCoalFlowChart () {
      if (!this.coalFlowChart) return
      const list = this.coalFlowStats || []
      const data = list.map((item) => ({
        name: item.dimensionName || item.name || this.coalFlowLabel(item),
        value: pickNumber(item, ['netWeight', 'netWeightSum', 'totalNetWeight', 'totalWeight', 'weight'])
      }))
      this.coalFlowChart.setOption(
        {
          tooltip: { trigger: 'item', formatter: '{b}: {c} 吨 ({d}%)' },
          legend: { bottom: 0, type: 'scroll' },
          series: [
            {
              name: '煤流向',
              type: 'pie',
              radius: ['42%', '66%'],
              center: ['50%', '43%'],
              data,
              label: { formatter: '{b}\n{d}%' }
            }
          ]
        },
        true
      )
    },
    async handleExportPdf () {
      const payload = this.buildPayload()
      if (!payload || this.exportLoading) return
      if ((this.tableTotal || 0) < 1) {
        this.$message.warning('无数据可导出')
        return
      }
      this.exportLoading = true
      try {
        const res = await exportPdf(payload)
        const blobData = res.data || res
        if (!blobData || (blobData.size !== undefined && blobData.size < 1)) {
          this.$message.warning('暂无可导出的报表数据')
          return
        }
        const blob = blobData instanceof Blob ? blobData : new Blob([blobData], { type: 'application/pdf' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `多维数据查询与智能分析_${Date.now()}.pdf`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        this.$message.success('PDF报表导出成功')
      } catch (e) {
        this.$message.error('PDF报表导出失败')
      } finally {
        this.exportLoading = false
      }
    },
    onResize () {
      const charts = [this.dateChart, this.cargoChart, this.customerChart, this.coalFlowChart]
      charts.forEach((chart) => {
        chart && chart.resize()
      })
    },
    coalFlowLabel (row) {
      const value = row && (row.cargoFlow !== undefined ? row.cargoFlow : (row.coalFlow !== undefined ? row.coalFlow : row.dimensionCode))
      if (String(value) === '1') return '仓库'
      if (String(value) === '2') return '中卫热电'
      return (row && (row.dimensionName || row.cargoFlowName || row.coalFlowName || row.flowName)) || '-'
    },
    coalFlowTag (row) {
      const value = row && (row.cargoFlow !== undefined ? row.cargoFlow : (row.coalFlow !== undefined ? row.coalFlow : row.dimensionCode))
      if (String(value) === '1') return 'success'
      if (String(value) === '2') return 'warning'
      return 'info'
    },
    flowTargetText (row) {
      if (!row) return '-'
      return (
        row.flowTargetName ||
        row.stockName ||
        row.storeName ||
        row.warehouseName ||
        row.custName ||
        row.customerName ||
        '-'
      )
    },
    fmtDateTime (row, col, value) {
      if (!value) return '-'
      const text = String(value)
      return text.length > 19 ? text.slice(0, 19) : text
    },
    fmtInteger (row, col, value) {
      if (value === null || value === undefined || value === '') return '-'
      return Number(value).toLocaleString('zh-CN', { maximumFractionDigits: 0 })
    },
    fmtTon (row, col, value) {
      return this.formatTonValue(value)
    },
    formatInteger (value) {
      const numberValue = Number(value || 0)
      return Number.isNaN(numberValue) ? '0' : numberValue.toLocaleString('zh-CN', { maximumFractionDigits: 0 })
    },
    formatTonValue (value) {
      if (value === null || value === undefined || value === '') return '0.00'
      const numberValue = Number(value)
      return Number.isNaN(numberValue) ? '0.00' : numberValue.toLocaleString('zh-CN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    }
  }
}
</script>

<style scoped lang="scss">
.multi-transport-page {
  padding: 0;
  background: transparent;
}

.head-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.filter-card,
.kpi-row,
.chart-row,
.block-card {
  margin-bottom: 16px;
}

.filter-title {
  margin-bottom: 12px;
  color: #303133;
  font-weight: 600;
}

.filter-form {
  background: transparent;
  padding: 0;
}

.weight-input {
  width: 96px;
}

.range-separator {
  display: inline-block;
  width: 20px;
  text-align: center;
  color: #909399;
}

.weight-unit {
  margin-left: 6px;
  color: #909399;
}

.kpi-card {
  position: relative;
  min-height: 118px;
  padding: 16px 10px 12px;
  text-align: center;
}

.kpi-icon {
  position: absolute;
  top: 12px;
  right: 12px;
  font-size: 22px;
  opacity: 0.35;
}

.kpi-val {
  font-size: 25px;
  font-weight: 700;
  line-height: 1.25;
}

.kpi-label {
  margin-top: 6px;
  color: #606266;
  font-size: 14px;
}

.kpi-sub {
  margin-top: 4px;
  color: #909399;
  font-size: 12px;
}

.kpi-blue .kpi-val {
  color: #409eff;
}

.kpi-green .kpi-val {
  color: #67c23a;
}

.kpi-gold .kpi-val {
  color: #e6a23c;
}

.kpi-red .kpi-val {
  color: #f56c6c;
}

.card-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.card-title {
  color: #303133;
  font-size: 15px;
  font-weight: 600;
}

.table-tip {
  color: #909399;
  font-size: 13px;
}

.echart-box {
  width: 100%;
  height: 320px;
}

.table-card {
  background: #fff;
}

.pager {
  margin-top: 12px;
  text-align: right;
}

@media (max-width: 1200px) {
  .weight-input {
    width: 86px;
  }
}
</style>
